import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Editor} from '@tinymce/tinymce-react';
import debounce from 'lodash/debounce';
import "tinymce/tinymce.min.js";
// Icons, theme, model, skin is required
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom/model";
import 'tinymce/skins/ui/oxide/skin.min.css';
// plugins
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';

import {convertHtmlToSlackMarkdown, initialProps} from './utils';
// For styling, check out email/components/BodyEditor.scss

type CallbackValue = {
  html: string;
  markdown: string;
}

type Callback = (value: CallbackValue) => void;

interface WYSIWYGEditorProps {
  defaultValue: string;
  onChange: Callback;
  disabled?: boolean;
  debounceTime?: number;
  editorProps?: any;
}

const WYSIWYGEditorComponent = forwardRef(({
  defaultValue,
  onChange,
  disabled = false,
  debounceTime = 800,
  editorProps = undefined,
}: WYSIWYGEditorProps, ref: any) => {
  const editorRef = useRef<any>(null);
  const [removed, setRemoved] = useState(false);
  const [initialValue, setInitialValue] = useState<string>(defaultValue || '');

  const initProps = useMemo(() => editorProps || initialProps(), [ ]);
  const debounceOnChange = useMemo(() => debounce(onChange, debounceTime), [onChange]);

  useEffect(() => {
    return () => {
      if (ref) {
        ref.current = null;
      }

      if (debounceOnChange && debounceOnChange.hasOwnProperty('cancel')) {
        debounceOnChange.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (removed) {
      /*
       * Force rerender
       */
      setRemoved(false);
    }
  }, [removed]);

  const onReInitEditor = () => {
    /*
     * Because we're about to remove the editor,
     *   re-set initial value to use the latest value (in case it changed).
     */
    const _content = editorRef.current.getContent();
    editorRef.current.destroy();
    setInitialValue(_content);
    setRemoved(true);
  };

  const onInit = (event: any, editor: any) => {
    setRemoved(false); // re-init
    if (ref) {
      /*
       * set `reInitEditor` on tinymce editor
       */
      editor.reInitEditor = onReInitEditor;
      ref.current = editor;
      editorRef.current = editor;
    }
  };

  const onRemove = (event: any, editor: any) => {
    ref.current = null;
    setRemoved(true);
  };

  const onEditorChange = (value: any, editor: any) => {
    if (editor.mode.isReadOnly()) {
      return;
    }
    let content = editor.getContent();
    debounceOnChange({
      html: content,
      markdown: convertHtmlToSlackMarkdown(content),
    });
  };

  if (removed) {
    /*
     * Force re-render in order to fix a BUG with iFrame being removed on DnD
     */
    return null;
  }

  return (
    <Editor
      onInit={onInit}
      onRemove={onRemove}
      onEditorChange={onEditorChange}
      init={initProps}
      initialValue={initialValue}
      disabled={disabled}
    />
  );
});

export const WYSIWYGEditor = React.memo(WYSIWYGEditorComponent);
